import { pushToDataLayer } from "../functions/gtm";
import {
  CartItemTypes,
  ICartItemType,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import {
  GtagActionField,
  IGTagUserData,
} from "./useEventTrackers/helpers/interfaces";
import { toSHA256Hex } from "@/utils/functions/toSHA256Hex";

export enum StandardPixelEvents {
  AddPaymentInfo = "AddPaymentInfo",
  AddToCart = "AddToCart",
  AddToWishlist = "AddToWishlist",
  CompleteRegistration = "CompleteRegistration",
  Contact = "Contact",
  CustomizeProduct = "CustomizeProduct",
  Donate = "Donate",
  FindLocation = "FindLocation",
  InitiateCheckout = "InitiateCheckout",
  Lead = "Lead",
  PageView = "PageView",
  Purchase = "Purchase",
  Schedule = "Schedule",
  Search = "Search",
  StartTrial = "StartTrial",
  SubmitApplication = "SubmitApplication",
  Subscribe = "Subscribe",
  ViewContent = "ViewContent",
}
//! we don't need it any more because if we need to collect custom Google Analytics
//! envent we should to connect it with Google Tag Manager
// export function useTrackEvent() {
//   const isClient = useIsClient();
//   const GA_INITIALIZED =
//     Object.keys((window as any).google_tag_manager || []).filter((e) => {
//       return e.substring(0, 2) == "G-";
//     }).length > 0;
//   const trackEvent = (event: any, data: any = null) => {
//     if (isClient) {
//       if (GA_INITIALIZED) {
//         (window as any)?.GA_MANAGER?.event({
//           category: "ecommerce",
//           action: event,
//           label: data?.content_name,
//           value: data?.content_ids ? data.content_ids[0] : data?.value,
//         });
//       }
//     }
//   };
//   return trackEvent;
// }
const getItemCategories = (categories_names: string[]) =>
  categories_names.length > 0
    ? categories_names.reduce(
        (prev, current, idx) => ({
          ...prev,
          [`item_category${idx === 0 ? "" : `_${idx}`}`]: current,
        }),
        {},
      )
    : {};
export const Gtag = (
  event: any,
  items: ICartItemType[],
  actionField: GtagActionField,
  affiliation: string,
  getPriceString: (price: number) => string,
  currency: string,
  customerData?: any,
) => {
  const itemsToPost = [
    ...items?.map((item) => ({
      item_id:
        item.type === CartItemTypes.product
          ? (item?.others?.parent_id ?? item.inner_id)
          : item.inner_id,
      item_name: item?.parent?.name ?? item.name,
      item_sku: item?.parent?.sku ?? "",
      affiliation: affiliation,
      coupon: actionField.coupon,
      discount: +getPriceString(item.discount),
      index: item.id,
      price: +getPriceString(item.price),
      quantity: item.quantity,
      item_variant: (item.type === CartItemTypes.product
        ? item?.others?.attrs_values.map((attr) => attr.name)
        : []
      ).join(","),
      ...getItemCategories(item?.categories_names ?? []),
    })),
  ];
  const skipConv = !!actionField?.skipConversion;
  const actionFieldToPost = {
    ...actionField,
    value: skipConv
      ? +(actionField?.value ?? 0)
      : +getPriceString(actionField?.value!),
    discount: +getPriceString(actionField?.discount!),
    shipping: +getPriceString(actionField?.shipping!),
    tax: +getPriceString(actionField?.tax!),
    currency: currency,
  };
  delete (actionFieldToPost as any).skipConversion;
  // Diagnostic only — see the purchase log below. Deliberately NOT used to gate the
  // pushes: `google_tag_manager` is populated by gtm.js when it finishes loading, so
  // gating on it silently discarded every event fired before then. `dataLayer` is a
  // queue and GTM replays it on init — push unconditionally. See functions/gtm.ts.
  const GTM_INITIALIZED =
    typeof window !== "undefined" &&
    (!!(window as any).google_tag_manager || !!(window as any).dataLayer);

  pushToDataLayer({ ecommerce: null }); // Clear the previous ecommerce object.
  pushToDataLayer({
    event: event,
    ecommerce: {
      ...actionFieldToPost,
      items: [...itemsToPost],
    },
    customer: {
      city: customerData?.city ? toSHA256Hex(customerData?.city) : undefined,
      email_hash: customerData?.email_hash || undefined,
      phone_hash: customerData?.phone_hash || undefined,
      external_id: customerData?.external_id || undefined,
      first_name: customerData?.first_name
        ? toSHA256Hex(customerData?.first_name)
        : undefined,
      last_name: customerData?.last_name
        ? toSHA256Hex(customerData?.last_name)
        : undefined,
      first_name_hash: customerData?.first_name_hash || undefined,
      last_name_hash: customerData?.last_name_hash || undefined,
      area: customerData?.area ? toSHA256Hex(customerData?.area) : undefined,
      country: customerData?.country
        ? toSHA256Hex(customerData?.country)
        : undefined,
    },
  });

  // ------------------------------------------------------------------
  // Track GA4 Purchase Events for debugging issues
  if (String(event).toLocaleLowerCase() === "purchase") {
    let windowInfo = {};
    if (typeof window !== "undefined") {
      try {
        windowInfo = {
          google_tag_manager_keys: Object.keys(
            (window as any).google_tag_manager || {},
          ),
          dataLayer: (window as any).dataLayer,
          dataLayer_length: (window as any).dataLayer?.length,
          userAgent: window.navigator?.userAgent,
          vendor: window.navigator?.vendor,
          language: window.navigator?.language,
        };
      } catch (e) {}
    }

    const logDetails = {
      event_name: event,
      GTM_INITIALIZED,
      cookies: typeof document !== "undefined" ? document.cookie : "",
      window_info: windowInfo,
      ecommerce_payload: {
        ...actionFieldToPost,
        items: itemsToPost,
      },
      customerData,
    };

    fetch("/api/log-info", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: "GA4/GTM Purchase Event Try",
        details: logDetails,
        host:
          typeof window !== "undefined"
            ? window.location.hostname
            : "next-server",
        url: typeof window !== "undefined" ? window.location.pathname : "",
      }),
    }).catch((e) => console.error("Error logging GA4/GTM Purchase Event", e));
  }
  // ------------------------------------------------------------------
};

export const GTagUserLoginEvent = (
  isLogedin: boolean,
  userdata: IGTagUserData,
) => {
  pushToDataLayer({
    event: "userLogin",
    user_login: isLogedin,
    userdata: {
      ...userdata,
    },
  });
};
export const GTagSearchEvent = (
  search: string,
  search_result: "found" | "not found",
) => {
  pushToDataLayer({
    event: "search_event",
    search: search,
    search_result: search_result,
  });
};

export const GTagMaillistConversionEvent = (email: string) => {
  pushToDataLayer({
    event: "maillist_conversion",
    email: email,
    email_hash: email ? toSHA256Hex(email) : "",
  });
};

export const GTagLeadEvent = (userdata: IGTagUserData) => {
  pushToDataLayer({
    event: "generate_lead",
    userdata: { ...userdata },
  });
};

export const GTagContactEvent = (userdata: IGTagUserData) => {
  pushToDataLayer({
    event: "contact",
    userdata: { ...userdata },
  });
};

export const GTagCompleteRegistrationEvent = (userdata: IGTagUserData) => {
  pushToDataLayer({
    event: "sign_up",
    userdata: { ...userdata },
  });
};
